/**
 * InvoicesPage — Händler-Rechnungsverwaltung (Phase 1 KFZ-DMS).
 *
 * Funktionsumfang:
 *   - Kundenstamm-Liste mit Quick-Anlage
 *   - Rechnungs-Liste mit Status-Badges (Entwurf, Final, Bezahlt, Gemahnt)
 *   - Quick-Anlage einer Rechnung aus einem verkauften Inserat
 *   - PDF-Download je Rechnung
 *   - Finalisierung (vergibt lückenlose Nummer)
 *   - Manuelles Markieren als bezahlt
 *
 * Aufruf: /dealer/invoices (Header-Link für B2B sichtbar)
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, UserPlus, Download, Lock, CheckCircle2, AlertTriangle, Receipt, Ban, Bell, Mail, Search, ChevronDown, ChevronRight, Filter, Wrench, Bike, MessageCircle, X, Building2, Copy, Trash2, MapPin } from "lucide-react";
import { addressIssues } from "../lib/addressCheck";
import { CustomerAddressQuickFix } from "../components/CustomerAddressQuickFix";

import api, { setApiOrgBranch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SettingsShortcut } from "../components/dealer/SettingsShortcut";
import EmployeeContextBanner from "../components/vermittler/EmployeeContextBanner";
import { employerAllows } from "../lib/employerModules";
import OrgBranchSwitcher from "../components/org/OrgBranchSwitcher";
import { ReactivationCard } from "../components/dealer/ReactivationCard";
import { previewPdfResponse, customerRecipient } from "../lib/pdfPreview";
import DirectSaleQrCard from "../components/invoices/DirectSaleQrCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { ServiceRemindersCard } from "../components/dealer/ServiceRemindersCard";
import { fmtEur, fmtDate, waNumber, STATUS_LABEL, ModalShell } from "./invoices/invoicesShared";
import SendInvoiceEmailModal from "./invoices/SendInvoiceEmailModal";
import { CreateInvoiceModal } from "./invoices/CreateInvoiceModal";
import {
    VehiclesTab, BranchBreakdown, CustomerSegmentBar, CustomerList, CreateCustomerModal,
} from "./invoices/customerComponents";

export default function InvoicesPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(() => searchParams.get("tab") || "invoices"); // invoices | customers | vehicles
    const [vehiclesTotal, setVehiclesTotal] = useState(0);

    // Jun-2026 — Header-Links (?tab=customers|vehicles) schalten den Tab auch dann
    // um, wenn die Seite bereits offen ist (sonst kein Remount → kein Tab-Wechsel).
    useEffect(() => {
        const t = searchParams.get("tab");
        if (t && ["invoices", "drafts", "customers", "vehicles"].includes(t)) setTab(t);
        // Jul-2026 (User-Wunsch) — Deep-Link von der FIN-Duplikat-Warnung:
        // ?q=<Suchbegriff> öffnet die Kunden-/Fahrzeugliste vorgefiltert.
        const q = searchParams.get("q");
        if (q) setSearchQ(q);
    }, [searchParams]);

    useEffect(() => {
        let cancel = false;
        api.get("/dealer/vehicles?limit=1").then(({ data }) => {
            if (!cancel) setVehiclesTotal(data.total || 0);
        }).catch(() => {});
        return () => { cancel = true; };
    }, []);
    const [invoices, setInvoices] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [cleaningTests, setCleaningTests] = useState(false);
    const [isDirectSaleHint, setIsDirectSaleHint] = useState(false);
    // Jun-2026 (User-Wunsch): Handy-QR startet Direktverkauf mit Kamera-EAN-Scan.
    const [autoScanHint, setAutoScanHint] = useState(false);
    // Direktsprung „Rechnung für diesen Kunden" aus der Kundenkarte
    const [invoiceCustomer, setInvoiceCustomer] = useState(null);
    const openInvoiceFor = useCallback((customer) => {
        setInvoiceCustomer(customer);
        setIsDirectSaleHint(false);
        setCreateOpen(true);
    }, []);
    // Phase 4e.I — Universal-Suchfeld
    const [searchQ, setSearchQ] = useState(() => searchParams.get("q") || "");
    const [newCustOpen, setNewCustOpen] = useState(false);
    const [stornoTarget, setStornoTarget] = useState(null); // {id, invoice_number, total}
    const [creditTarget, setCreditTarget] = useState(null); // Rechnung für Teil-Gutschrift (Jun-2026, User-Wunsch)
    const [reminderTarget, setReminderTarget] = useState(null); // {id, invoice_number, total, reminders:[]}
    const [emailTarget, setEmailTarget] = useState(null); // Rechnung für E-Mail-Versand mit Anhängen (Jul-2026)
    const [filterStatus, setFilterStatus] = useState("active"); // active | all | cancelled
    const [segmentResult, setSegmentResult] = useState(null); // null = kein Segment aktiv
    // Cross-Branch (Hauptsitz↔Filiale) — Standort-Umschalter für Kunden/Fahrzeuge
    const [orgBranch, setOrgBranch] = useState(null);
    // Server-seitige Kundensuche (Bestände >2000 vollständig durchsuchbar)
    const [custTotal, setCustTotal] = useState(0);
    const [custSearchResults, setCustSearchResults] = useState(null); // null = keine Server-Suche/Filter aktiv
    const [custSearching, setCustSearching] = useState(false);
    const [custMissing, setCustMissing] = useState(""); // "" | email | phone | vehicle
    const [toolsOpen, setToolsOpen] = useState(false); // „Werkzeuge & Auswertungen" eingeklappt
    const [custPage, setCustPage] = useState(1); // serverseitige Paginierung (1-indexiert)
    const [custPageSize, setCustPageSize] = useState(20);

    // Debounced serverseitige Kundensuche + „Fehlende Daten"-Filter (nur im Kunden-Tab)
    useEffect(() => {
        if (tab !== "customers") return;
        const term = searchQ.trim();
        if (!term && !custMissing) { setCustSearchResults(null); setCustSearching(false); return; }
        setCustSearching(true);
        const handle = setTimeout(async () => {
            try {
                const p = new URLSearchParams({ limit: "500" });
                if (term) p.set("q", term);
                if (custMissing) p.set("missing", custMissing);
                const r = await api.get(`/customers?${p.toString()}`);
                setCustSearchResults(r.data || []);
            } catch { setCustSearchResults([]); }
            setCustSearching(false);
        }, 350);
        return () => clearTimeout(handle);
    }, [searchQ, tab, orgBranch, custMissing]);

    // Header-Scope beim Verlassen der Seite wieder zurücksetzen
    useEffect(() => () => setApiOrgBranch(null), []);

    // Standort-Deep-Link aus dem Org-Cockpit (?branch=<dealer_id>) — Jun-2026.
    useEffect(() => {
        const br = new URLSearchParams(window.location.search).get("branch");
        if (br) { setOrgBranch(br); setApiOrgBranch(br); }
    }, []);

    const loadCustomersPage = useCallback(async (p, size) => {
        try {
            const params = new URLSearchParams({ limit: String(size), skip: String((p - 1) * size) });
            const c = await api.get(`/customers?${params.toString()}`);
            setCustomers(c.data || []);
            const total = parseInt(c.headers?.["x-total-count"] || "", 10);
            setCustTotal(Number.isNaN(total) ? (c.data || []).length : total);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunden laden fehlgeschlagen");
        }
    }, []);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const i = await api.get("/invoices");
            setInvoices(i.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user === false) navigate("/login");
        else if (user && user.role !== "b2b" && user.role !== "admin" && !employerAllows(user, "accounting")) navigate("/");
        if (user) loadAll();
    }, [user, navigate, loadAll]);

    // Kundenliste serverseitig paginieren (lädt jeweils custPageSize Datensätze).
    useEffect(() => {
        if (user) loadCustomersPage(custPage, custPageSize);
    }, [user, custPage, custPageSize, orgBranch, loadCustomersPage]);

    // Jun-2026 (User-Wunsch) — Direktverkauf direkt aus dem Header-Dropdown:
    // /dealer/invoices?action=direct-sale|new-invoice|datev — Direkteinstieg aus dem Header.
    useEffect(() => {
        if (!user) return;
        const params = new URLSearchParams(window.location.search);
        // Jul-2026 (User-Wunsch): /dealer/invoices?q=RG-… — Suche vorbefüllen
        // (z. B. Klick auf einen Beleg im SumUp-Abgleich).
        const q = params.get("q");
        if (q) {
            setSearchQ(q);
            navigate("/dealer/invoices", { replace: true });
            return;
        }
        const action = params.get("action");
        if (!action) return;
        if (action === "direct-sale") {
            // Jul-2026 (User-Wunsch): QR trägt den Standort — der Verkauf wird dem
            // Standort zugeordnet, an dem der QR-Code erzeugt wurde.
            const branchParam = params.get("branch");
            if (branchParam) { setApiOrgBranch(branchParam); setOrgBranch(branchParam); }
            setIsDirectSaleHint(true);
            setAutoScanHint(params.get("scan") === "1");
            setCreateOpen(true);
        } else if (action === "new-invoice") {
            setIsDirectSaleHint(false);
            setCreateOpen(true);
        } else if (action === "new-customer") {
            setNewCustOpen(true);
        }
        navigate("/dealer/invoices", { replace: true });
    }, [user, navigate]);   

    const [finalizeConfirmId, setFinalizeConfirmId] = useState(null);
    // Aug-2026 (User-Wunsch) — Entwürfe löschen (mit Bestätigungs-Doppelklick)
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const deleteDraft = async (id) => {
        if (deleteConfirmId !== id) {
            setDeleteConfirmId(id);
            toast.info("Entwurf unwiderruflich löschen? Zum Bestätigen erneut klicken.");
            return;
        }
        setDeleteConfirmId(null);
        try {
            await api.delete(`/invoices/${id}`);
            toast.success("Entwurf gelöscht");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    };
    const finalizeInvoice = async (id) => {
        if (finalizeConfirmId !== id) {
            setFinalizeConfirmId(id);
            // Aug-2026 (User-Wunsch): unvollständige Kundenanschrift vor dem
            // Festschreiben deutlich melden (Hausnummer, Name, Straße, PLZ, Ort).
            const inv = invoices.find((i) => i.id === id);
            const iss = addressIssues(customers.find((c) => c.id === inv?.customer_id));
            if (iss.length) {
                toast.warning(`⚠ Kundenanschrift unvollständig (fehlt: ${iss.join(", ")}) — bitte Kundenakte prüfen. Trotzdem finalisieren? Zum Bestätigen erneut klicken.`, { duration: 9000 });
            } else {
                toast.info("Rechnung finalisieren? Danach kann sie nicht mehr verändert werden (GoBD). Zum Bestätigen erneut klicken.");
            }
            return;
        }
        setFinalizeConfirmId(null);
        try {
            await api.post(`/invoices/${id}/finalize`);
            toast.success("Rechnung finalisiert");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Finalize fehlgeschlagen"); }
    };

    const [b2bSendConfirmId, setB2bSendConfirmId] = useState(null);
    const finalizeSendB2B = async (id) => {
        if (b2bSendConfirmId !== id) {
            setB2bSendConfirmId(id);
            toast.info("Finalisiert die Rechnung unwiderruflich (GoBD, §14-Nummer) und sendet sie an den Käufer-Händler. Zum Bestätigen erneut klicken.");
            return;
        }
        setB2bSendConfirmId(null);
        try {
            const { data } = await api.post(`/invoices/${id}/finalize-send-b2b`);
            toast.success(`Rechnung ${data.invoice_number} finalisiert & an ${data.sent_to} gesendet`);
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Finalisieren & Senden fehlgeschlagen"); }
    };

    const markPaid = async (id, total) => {
        // Phase 4c+ · Skonto-Auto-Vorschlag laden
        let skontoHint = null;
        try {
            const r = await api.get(`/invoices/${id}/skonto-preview`);
            if (r.data?.eligible && r.data.skonto_amount > 0) {
                skontoHint = r.data;
            }
        } catch (_e) { /* skonto best-effort */ }

        // Wenn Skonto möglich, Anwender fragen
        let useSkonto = false;
        if (skontoHint) {
            const msg = `💰 Skonto möglich: ${skontoHint.skonto_percent} % = ${fmtEur(skontoHint.skonto_amount)} Abzug.\n\n` +
                `Frist: ${skontoHint.deadline} (noch ${skontoHint.days_left} Tage)\n\n` +
                `JA = Skonto anwenden (Kunde zahlt ${fmtEur(skontoHint.amount_to_pay)} statt ${fmtEur(skontoHint.remaining)})\n` +
                `NEIN = Vollen Betrag verbuchen`;
            useSkonto = window.confirm(msg);
        }

        const defaultAmount = useSkonto && skontoHint ? skontoHint.amount_to_pay : total;
        const amt = prompt(`Bezahlten Betrag eingeben (Vorgabe ${fmtEur(defaultAmount)}):`, defaultAmount);
        if (!amt) return;
        try {
            const payload = { paid_amount: Number(amt) };
            if (useSkonto && skontoHint) payload.skonto_applied = skontoHint.skonto_amount;
            await api.post(`/invoices/${id}/mark-paid`, payload);
            toast.success(useSkonto ? `Bezahlt + Skonto ${fmtEur(skontoHint.skonto_amount)} gebucht` : "Als bezahlt markiert");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const sendEmail = (id) => {
        const inv = invoices.find((x) => x.id === id);
        if (inv) setEmailTarget(inv);
    };

    // Aug-2026 (User-Wunsch) — Zahlart nachträglich setzen
    const setPayTerms = async (id, code) => {
        try {
            await api.patch(`/invoices/${id}/payment-terms`, { payment_terms_code: code || null });
            toast.success(code ? "Zahlart gespeichert" : "Zahlart entfernt");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Zahlart konnte nicht gespeichert werden"); }
    };

    const cancelInvoice = async (id, reason) => {
        try {
            const res = await api.post(`/invoices/${id}/cancel`, { reason });
            toast.success(`Storno-Rechnung ${res.data?.invoice_number || ""} erstellt`);
            setStornoTarget(null);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Storno fehlgeschlagen");
        }
    };

    const sendReminder = async (id, payload) => {
        try {
            const res = await api.post(`/invoices/${id}/reminder`, payload);
            const lvl = res.data?.reminder?.level;
            const label = lvl === 1 ? "Zahlungserinnerung" : lvl === 2 ? "1. Mahnung" : "2. Mahnung (letzte)";
            let msg = `${label} angelegt`;
            if (res.data?.email?.sent) {
                msg += res.data.email.mocked ? " · Mail (Mock) versendet" : ` · Mail an ${res.data.email.to}`;
            } else if (payload.send_email && res.data?.email?.reason) {
                msg += ` · Mail nicht möglich: ${res.data.email.reason}`;
            }
            toast.success(msg);
            setReminderTarget(null);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Mahnung fehlgeschlagen");
        }
    };

    const _invRecipient = (inv) => (inv ? {
        name: inv.customer_name || inv.customer?.name || "",
        email: inv.customer_email || inv.customer?.email || "",
        phone: inv.customer_phone || inv.customer?.phone || "",
    } : {});

    const downloadReminderPdf = async (invId, level, invNumber) => {
        try {
            const res = await api.get(`/invoices/${invId}/reminder/${level}/pdf`, { responseType: "blob" });
            const inv = invoices.find((x) => x.id === invId);
            previewPdfResponse(res, `Mahnung_St${level}_${invNumber || "Rechnung"}.pdf`, { recipient: _invRecipient(inv) });
        } catch { toast.error("Mahnungs-PDF konnte nicht geladen werden"); }
    };

    // Jul-2026 (User-Wunsch) — Button-Aktionen protokollieren → grün gefüllt
    // (erst NACH erfolgreichem Server-Update, kein falsches Grün bei Fehlern).
    const trackAction = (id, action) => {
        api.post(`/invoices/${id}/track-action`, { action })
            .then(() => {
                setInvoices((prev) => prev.map((x) => x.id === id
                    ? { ...x, action_log: { ...(x.action_log || {}), [action]: new Date().toISOString() } }
                    : x));
            })
            .catch(() => {});
    };

    const downloadPdf = async (id, number) => {
        try {
            const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
            const inv = invoices.find((x) => x.id === id);
            previewPdfResponse(res, `Rechnung_${number || "ENTWURF"}.pdf`, { recipient: _invRecipient(inv) });
            trackAction(id, "pdf");
        } catch { toast.error("PDF konnte nicht geladen werden"); }
    };

    const downloadXRechnung = async (id, number, bundle = false) => {
        try {
            const path = bundle ? "xrechnung-bundle.zip" : "xrechnung.xml";
            const mime = bundle ? "application/zip" : "application/xml";
            const res = await api.get(`/invoices/${id}/${path}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
            const a = document.createElement("a");
            a.href = url;
            a.download = bundle
                ? `XRechnung_Bundle_${number || id}.zip`
                : `XRechnung_${number || id}.xml`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            trackAction(id, bundle ? "zip" : "xml");
            toast.success(bundle ? "ZIP-Bundle heruntergeladen" : "XRechnung-XML heruntergeladen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "XRechnung-Download fehlgeschlagen (nur für finalisierte Rechnungen)");
        }
    };

    if (!user) return <div className="p-8">Lade…</div>;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="invoices-page">
            <EmployeeContextBanner />
            {/* Header band */}
            <div className="bg-[#111827] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
                    <div className="flex items-start justify-between gap-6">
                        <div className="flex-1 min-w-0">
                            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight inline-flex items-center gap-2">Rechnungen & Kunden <SettingsShortcut to="/dashboard?tab=settings#settings-profile" title="Stammdaten & Rechnungs-Einstellungen" testid="settings-shortcut-rechnungen" dark /></h1>
                            {/* Aug-2026 (User-Wunsch): §25a-Beschreibungstext entfernt, Header kompakter. */}
                        </div>
                        {/* Jun-2026 (User-Wunsch): sichtbare QR-Karte rechts (analog Auftragsübersicht).
                            Handy scannt → Direktverkauf mit Kamera-EAN-Scan startet. */}
                    </div>
                    {/* Jun-2026 (User-Wunsch): QR ganz rechts, links davon die zwei Button-Zeilen */}
                    <div className="flex items-end justify-end gap-2 md:gap-3 mt-2">
                        <div className="flex-1 flex flex-col items-end gap-1.5 min-w-0">
                    {/* Zeile 1: Navigation — rechtsbündig, Dashboard als Button ganz links */}
                    <div className="flex flex-wrap gap-2 items-center justify-end max-w-full" data-testid="inv-nav-row">
                        <Button
                            onClick={() => navigate("/dashboard")}
                            variant="outline"
                            className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white rounded-sm text-xs font-bold uppercase tracking-wider"
                            data-testid="back-to-dashboard"
                        >
                            ← Dashboard
                        </Button>
                        <Link to="/dealer/wms" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm inline-flex items-center gap-1.5" data-testid="link-to-wms">
                            Warenwirtschaft →
                        </Link>
                        <Link to="/dealer/workshop-orders" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm inline-flex items-center gap-1.5" data-testid="link-workshop-orders">
                            Auftrag →
                        </Link>
                        <Link to="/dealer/documents" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm inline-flex items-center gap-1.5" data-testid="link-document-outbox">
                            📂 Dokumenten-Center
                        </Link>
                    </div>
                    {/* Zeile 2: Funktionen dieser Seite — rechtsbündig */}
                    <div className="flex flex-wrap gap-2 justify-end max-w-full" data-testid="inv-action-row">
                        <Button variant="outline" onClick={() => setNewCustOpen(true)} className="bg-white text-[#111827] hover:bg-gray-100 rounded-sm font-bold" data-testid="new-customer-btn">
                            <UserPlus className="w-4 h-4 mr-1.5" /> Neuer Kunde
                        </Button>
                        {/* Phase 4g.D (Feb 2026, User-Wunsch) — Direktverkauf-Button grün als positive Aktion. */}
                        <Button onClick={() => { setIsDirectSaleHint(true); setCreateOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm" data-testid="new-direct-sale-btn">
                            <Plus className="w-4 h-4 mr-1.5" /> Direktverkauf
                        </Button>
                        <Button onClick={() => { setIsDirectSaleHint(false); setCreateOpen(true); }} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="new-invoice-btn">
                            <Plus className="w-4 h-4 mr-1.5" /> Neue Rechnung
                        </Button>
                        {user?.branch_role === "hauptsitz" && (
                            <Button variant="outline" disabled={cleaningTests}
                                onClick={async () => {
                                    if (!window.confirm("Die benannten Test-Belege (RG-2026-000004-FBWE, Gutschrift RG-2026-000001-FRAL + 4 Fahrzeug-Entwürfe) jetzt endgültig löschen?\n\nEin Backup wandert in den Papierkorb.")) return;
                                    setCleaningTests(true);
                                    try {
                                        const r = await api.post("/invoices/cleanup-test-documents");
                                        toast.success(r.data.message);
                                    } catch (e) { toast.error(e?.response?.data?.detail || "Bereinigung fehlgeschlagen"); }
                                    finally { setCleaningTests(false); loadAll(); }
                                }}
                                className="border-red-300 text-red-700 hover:bg-red-50 font-bold rounded-sm"
                                data-testid="cleanup-test-invoices-btn">
                                <Trash2 className="w-4 h-4 mr-1.5" /> Test-Belege bereinigen
                            </Button>
                        )}
                    </div>
                        </div>
                        <div className="shrink-0" data-testid="invoices-header-qr">
                            <DirectSaleQrCard />
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
                <div className="flex gap-2 border-b border-gray-200 overflow-x-auto whitespace-nowrap [&>button]:shrink-0">
                    {[
                        { k: "invoices", l: "Rechnungen", count: invoices.filter((i) => i.status !== "draft").length, icon: Receipt },
                        // Aug-2026 (User-Wunsch): Entwürfe in eigenem Tab, nicht unter Rechnungen
                        { k: "drafts", l: "Entwürfe", count: invoices.filter((i) => i.status === "draft").length, icon: FileText },
                        { k: "customers", l: "Kunden", count: custTotal || customers.length, icon: UserPlus },
                        { k: "vehicles", l: "Fahrzeuge", count: vehiclesTotal, icon: Bike },
                    ].map((t) => (
                        <button
                            key={t.k}
                            onClick={() => setTab(t.k)}
                            className={`px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors inline-flex items-center gap-2 ${tab === t.k ? "border-[#111827] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                            data-testid={`tab-${t.k}`}
                        >
                            <t.icon className="w-4 h-4" /> {t.l}
                            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-sm text-[10px]">{t.count}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                {/* Phase 4e.I — Universal-Suchfeld über alle Felder
                    Phase 4g.K (Feb 2026, User-Wunsch): grün hervorgehoben für bessere Sichtbarkeit */}
                <div className="mb-4 flex items-center gap-2">
                    <div className="relative flex-1 max-w-xl">
                        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-emerald-600" />
                        <Input
                            value={searchQ}
                            onChange={(e) => setSearchQ(e.target.value)}
                            placeholder={tab === "invoices" ? "Universal-Suche Rechnungen (Nummer, Position, Kunde, Code…)" : tab === "drafts" ? "Universal-Suche Entwürfe (Position, Kunde, Fahrzeug…)" : tab === "vehicles" ? "Fahrzeuge suchen (Titel, Modell, Hersteller, FIN, Kennzeichen, Bestellcode, Standort…)" : "Universal-Suche Kunden (Name, Firma, E-Mail, Adresse, USt-IdNr…)"}
                            className="pl-9 rounded-sm h-9 bg-white border-2 border-emerald-500 focus-visible:ring-emerald-400 placeholder:text-emerald-700/70"
                            data-testid="invoices-universal-search"
                        />
                    </div>
                    {searchQ && (
                        <Button variant="outline" onClick={() => setSearchQ("")} className="h-9 rounded-sm inline-flex items-center gap-1.5 border-red-300 bg-red-50 text-red-700 hover:bg-red-100 font-bold uppercase tracking-wider text-[11px]" data-testid="invoices-search-clear">
                            <X className="w-3.5 h-3.5" /> Zurücksetzen
                        </Button>
                    )}
                </div>
                {loading ? (
                    <div className="text-center py-12 text-gray-500">Lade…</div>
                ) : tab === "invoices" || tab === "drafts" ? (
                    <>
                    {user?.branch_role === "hauptsitz" && orgBranch && (
                        <div className="mb-3 bg-[#FFCC00]/15 border border-[#FFCC00] rounded-sm px-3 py-2 flex items-center justify-between gap-2 text-sm" data-testid="invoices-branch-banner">
                            <span className="font-bold text-[#111827] inline-flex items-center gap-1.5">
                                <Building2 className="w-4 h-4" /> Gefiltert auf einen Standort
                            </span>
                            <button type="button" onClick={() => { setOrgBranch(null); setApiOrgBranch(null); navigate("/dealer/invoices"); }}
                                className="text-xs font-bold uppercase tracking-wider text-[#111827] hover:underline" data-testid="invoices-branch-clear">
                                Alle Standorte anzeigen
                            </button>
                        </div>
                    )}
                    <InvoiceList
                        invoices={(searchQ ? invoices.filter((i) => JSON.stringify(i).toLowerCase().includes(searchQ.toLowerCase())) : invoices)
                            .filter((i) => (tab === "drafts" ? i.status === "draft" : i.status !== "draft"))}
                        mode={tab}
                        customers={customers}
                        filterStatus={filterStatus}
                        onFilterChange={setFilterStatus}
                        onFinalize={finalizeInvoice}
                        finalizeConfirmId={finalizeConfirmId}
                        onDeleteDraft={deleteDraft}
                        deleteConfirmId={deleteConfirmId}
                        onFinalizeSendB2B={finalizeSendB2B}
                        b2bSendConfirmId={b2bSendConfirmId}
                        onMarkPaid={markPaid}
                        onSetPayTerms={setPayTerms}
                        onDownload={downloadPdf}
                        onDownloadXRechnung={downloadXRechnung}
                        onSendEmail={sendEmail}
                        onCancel={(inv) => setStornoTarget({ id: inv.id, invoice_number: inv.invoice_number, total: inv.totals?.total_gross })}
                        onCreditNote={(inv) => setCreditTarget(inv)}
                        onRemind={(inv) => setReminderTarget({ id: inv.id, invoice_number: inv.invoice_number, total: inv.totals?.total_gross, paid_amount: inv.paid_amount, reminders: inv.reminders || [] })}
                        onDownloadReminder={downloadReminderPdf}
                        onTrackAction={trackAction}
                        onOpenEntity={(kind, q) => { setSearchQ(q); if (kind === "customers") setCustPage(1); setTab(kind); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        onCustomerPatched={(u) => setCustomers((arr) => arr.map((c) => (c.id === u.id ? { ...c, ...u } : c)))}
                    />
                    {emailTarget && (
                        <SendInvoiceEmailModal
                            invoice={emailTarget}
                            customer={customers.find((c) => c.id === emailTarget.customer_id)}
                            onClose={() => setEmailTarget(null)}
                            onSent={() => { setEmailTarget(null); loadAll(); }}
                        />
                    )}
                    </>
                ) : tab === "customers" ? (
                    <>
                        {user?.branch_role === "hauptsitz" && (
                            <div className="mb-4 bg-white border border-gray-200 rounded-sm px-3 py-2" data-testid="customers-branch-bar">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Standort:</span>
                                    <OrgBranchSwitcher user={user} value={orgBranch} onChange={(b) => { setOrgBranch(b); setCustPage(1); }} allowAll allowReorder />
                                    <span className="text-[11px] text-gray-400">Hauptsitz sieht & bearbeitet Filial-Kunden</span>
                                </div>
                                <BranchBreakdown />
                            </div>
                        )}
                        {/* Jun-2026 (User-Wunsch) — alle Kunden-Werkzeuge in einem
                            aufklappbaren Bereich gebündelt (Erinnerungen, Segment,
                            Reaktivierung, Fehlende-Daten-Filter). */}
                        <div className="bg-white border border-gray-200 rounded-sm mb-4" data-testid="customer-tools">
                            <button type="button" onClick={() => setToolsOpen((o) => !o)}
                                className="w-full flex items-center gap-2 p-4 text-left" data-testid="customer-tools-toggle">
                                {toolsOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                <Wrench className="w-4 h-4 text-[#7C3AED]" />
                                <h3 className="font-bold text-sm">Werkzeuge & Auswertungen</h3>
                                <span className="text-[11px] text-gray-400 hidden sm:inline">— Erinnerungen (HU & Geburtstag) · Segment/Mailings · Inaktiv-Reaktivierung · Fehlende Daten</span>
                                {custMissing && <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-sm bg-[#111827] text-white" data-testid="customer-tools-active-filter">Filter aktiv</span>}
                            </button>
                            {toolsOpen && (
                                <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
                                    <ServiceRemindersCard />
                                    <CustomerSegmentBar onResult={setSegmentResult} />
                                    <ReactivationCard />
                                    <div className="flex items-center gap-2 flex-wrap" data-testid="customers-missing-filter">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">Fehlende Daten:</span>
                                        {[
                                            { k: "", l: "Alle" },
                                            { k: "email", l: "ohne E-Mail" },
                                            { k: "phone", l: "ohne Telefon" },
                                            { k: "vehicle", l: "ohne Fahrzeug" },
                                        ].map((m) => (
                                            <button key={m.k || "all"} type="button" onClick={() => setCustMissing(m.k)}
                                                className={`text-xs px-3 py-1.5 rounded-sm border font-medium ${custMissing === m.k ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                                                data-testid={`customers-missing-${m.k || "all"}`}>
                                                {m.l}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="mb-3 text-xs text-gray-500" data-testid="customers-count-line">
                            {segmentResult !== null ? (
                                <span>Segment: <b>{segmentResult.length}</b> Kunden</span>
                            ) : (searchQ.trim() || custMissing) ? (
                                custSearching
                                    ? <span>Suche im gesamten Kundenbestand…</span>
                                    : <span><b>{(custSearchResults || []).length}</b> Treffer{custMissing ? ` (Filter: ${custMissing === "email" ? "ohne E-Mail" : custMissing === "phone" ? "ohne Telefon" : "ohne Fahrzeug"})` : ""}{searchQ.trim() ? ` für „${searchQ.trim()}"` : ""}</span>
                            ) : (
                                <span>
                                    <b>{custTotal.toLocaleString("de-DE")}</b> Kunden im Bestand
                                </span>
                            )}
                        </div>
                        <CustomerList
                            customers={
                                segmentResult !== null
                                    ? segmentResult
                                    : ((searchQ.trim() || custMissing) ? (custSearchResults || []) : customers)
                            }
                            onChange={() => loadCustomersPage(custPage, custPageSize)}
                            onCreateInvoice={openInvoiceFor}
                            onCreateOrder={(c) => navigate(`/dealer/workshop-orders?action=new&customer=${c.id}`)}
                            serverPagination={
                                (segmentResult === null && !searchQ.trim() && !custMissing)
                                    ? {
                                        total: custTotal, page: custPage, pageSize: custPageSize,
                                        onPage: setCustPage,
                                        onPageSize: (s) => { setCustPageSize(s); setCustPage(1); },
                                    }
                                    : null
                            }
                        />
                    </>
                ) : (
                    <VehiclesTab searchQ={searchQ} onTotal={setVehiclesTotal} initialMissing={searchParams.get("missing") || ""} />
                )}
            </div>

            {createOpen && (
                <CreateInvoiceModal
                    customers={invoiceCustomer && !customers.some((c) => c.id === invoiceCustomer.id) ? [invoiceCustomer, ...customers] : customers}
                    initialDirectSale={isDirectSaleHint}
                    initialAutoScan={autoScanHint}
                    initialCustomerId={invoiceCustomer?.id || ""}
                    onClose={() => { setCreateOpen(false); setInvoiceCustomer(null); setAutoScanHint(false); }}
                    onCreated={() => { setCreateOpen(false); setInvoiceCustomer(null); setAutoScanHint(false); loadAll(); }}
                />
            )}
            {newCustOpen && (
                <CreateCustomerModal
                    onClose={() => setNewCustOpen(false)}
                    onCreated={() => { setNewCustOpen(false); setCustPage(1); loadCustomersPage(1, custPageSize); }}
                />
            )}
            {creditTarget && (
                <CreditNoteModal
                    invoice={creditTarget}
                    customer={customers.find((c) => c.id === creditTarget.customer_id)}
                    onClose={() => setCreditTarget(null)}
                    onCreated={() => { setCreditTarget(null); loadAll(); }}
                />
            )}
            {stornoTarget && (
                <StornoModal
                    target={stornoTarget}
                    onClose={() => setStornoTarget(null)}
                    onConfirm={(reason) => cancelInvoice(stornoTarget.id, reason)}
                />
            )}
            {reminderTarget && (
                <ReminderModal
                    target={reminderTarget}
                    onClose={() => setReminderTarget(null)}
                    onConfirm={(payload) => sendReminder(reminderTarget.id, payload)}
                />
            )}
        </div>
    );
}

// Aug-2026 (User-Wunsch) — Hover-Kopierfunktion (FIN, Rechnungsnummer)
function copyToClipboard(value) {
    const fallback = () => {
        const ta = document.createElement("textarea");
        ta.value = value;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch { /* ignorieren */ }
        document.body.removeChild(ta);
    };
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value).catch(fallback);
    } else { fallback(); }
    toast.success(`Kopiert: ${value}`);
}

function CopyBtn({ value, revealClass = "", testId }) {
    return (
        <button type="button" title={`Kopieren: ${value}`}
            onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(value);
            }}
            className={`opacity-0 ${revealClass} transition-opacity text-gray-400 hover:text-[#111827] shrink-0`}
            data-testid={testId}>
            <Copy className="w-3 h-3" />
        </button>
    );
}

// Aug-2026 (User-Wunsch) — Zahlart nachträglich setzen (Rechnungs-Center)
const PAY_TERM_OPTIONS = [
    ["barzahlung", "Barzahlung"],
    ["ec", "EC-Karte"],
    ["ueberweisung", "Überweisung"],
    ["sepa", "SEPA-Einzug"],
    ["finanzierung_hausbank", "Finanzierung Hausbank"],
    ["finanzierung_santander", "Finanzierung Santander"],
    ["leasing_santander", "Leasing Santander"],
];

// Aug-2026 (User-Bug): Alt-Rechnungen haben nur payment_method (Text) ohne
// payment_terms_code — Zahlart trotzdem im Dropdown anzeigen.
const PAY_METHOD_TO_CODE = {
    "ec-karte": "ec", "ec": "ec", "ec karte": "ec",
    "barzahlung": "barzahlung", "bar": "barzahlung",
    "überweisung": "ueberweisung", "ueberweisung": "ueberweisung",
    "sepa": "sepa", "sepa-einzug": "sepa", "sepa-lastschrift": "sepa",
};
const payCodeOf = (inv) => inv.payment_terms_code
    || PAY_METHOD_TO_CODE[String(inv.payment_method || "").trim().toLowerCase()] || "";

function InvoiceList({ invoices, customers, mode = "invoices", filterStatus, onFilterChange, onFinalize, finalizeConfirmId, onDeleteDraft, deleteConfirmId, onFinalizeSendB2B, b2bSendConfirmId, onMarkPaid, onSetPayTerms, onDownload, onDownloadXRechnung, onSendEmail, onCancel, onCreditNote, onRemind, onDownloadReminder, onTrackAction, onOpenEntity, onCustomerPatched }) {
    // Aug-2026 (User-Wunsch): Kunden-Schnellkorrektur direkt aus der Adress-Warnung
    const [addrFixInvId, setAddrFixInvId] = useState(null);
    const onAddrFixToggle = (id) => setAddrFixInvId((cur) => (cur === id ? null : id));
    // Jul-2026 (User-Wunsch) — „Bezahlt" buchen darf nur die Buchhaltung.
    const { user } = useAuth();
    const canMarkPaid = user?.role === "b2b" || user?.role === "admin" || user?.role === "vermittler"
        || (user?.resolved_modules || []).some((m) => ["buchhaltung", "filialleitung", "geschaeftsfuehrung"].includes(m));
    const custById = Object.fromEntries(customers.map((c) => [c.id, c]));
    const invByNumber = Object.fromEntries(invoices.filter((i) => i.invoice_number).map((i) => [i.invoice_number, i]));

    // Jun-2026 (User-Wunsch) — Rechnung per WhatsApp senden: öffnet wa.me mit
    // vorbefülltem Text (Rechnungsnr. + Betrag) + 1-Klick-Stripe-Zahlungslink
    // (`/zahlung/{token}` → Stripe → Webhook markiert „bezahlt"). Funktioniert
    // live ohne API-Key; die PDF lädt der Händler separat herunter.
    const waSendInvoice = async (inv) => {
        const cust = custById[inv.customer_id];
        const num = waNumber(cust?.phone);
        if (!num) { toast.error("Kunde hat keine gültige Telefonnummer für WhatsApp"); return; }
        // Fenster synchron öffnen (Popup-Blocker), URL nach Token-Abruf setzen.
        const win = window.open("", "_blank", "noopener");
        const name = (cust.first_name || cust.company_name || "").trim();
        const total = Number(inv.totals?.total_gross || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
        // Zahlungs-Token sicherstellen und Bezahllink anhängen (außer bereits bezahlt).
        let payLine = "";
        try {
            const { data } = await api.post(`/invoices/${inv.id}/payment-token`);
            if (data?.token && !data?.already_paid) {
                const link = `${window.location.origin}/zahlung/${data.token}`;
                payLine = `\n\nBequem online bezahlen (Kreditkarte/Sofort) mit 1 Klick:\n${link}`;
            }
        } catch (e) {
            // Bezahllink optional — WhatsApp trotzdem öffnen.
        }
        const text = `Hallo ${name}, Ihre Rechnung ${inv.invoice_number} über ${total} liegt vor. Die Rechnung als PDF senden wir Ihnen direkt im Anschluss. Vielen Dank!${payLine}`;
        const url = `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
        if (win) { win.location.href = url; } else { window.open(url, "_blank", "noopener"); }
        onTrackAction?.(inv.id, "whatsapp");
        toast.success(payLine ? "WhatsApp geöffnet — inkl. Stripe-Bezahllink. PDF bei Bedarf anhängen." : "WhatsApp geöffnet — PDF bei Bedarf herunterladen und im Chat anhängen.");
    };

    // Filter logic (Entwürfe-Tab: keine Storno-Filter nötig)
    const visible = invoices.filter((inv) => {
        if (mode === "drafts") return true;
        if (filterStatus === "all") return true;
        if (filterStatus === "cancelled") return inv.status === "cancelled" || inv.is_storno;
        // "active" = alles außer storniert / Storno-Rechnungen
        return inv.status !== "cancelled" && !inv.is_storno;
    });

    const counts = {
        active: invoices.filter((i) => i.status !== "cancelled" && !i.is_storno).length,
        cancelled: invoices.filter((i) => i.status === "cancelled" || i.is_storno).length,
        all: invoices.length,
    };

    if (!invoices.length) return (
        <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid={mode === "drafts" ? "drafts-empty" : "invoices-empty"}>
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <div className="font-display font-black text-lg">{mode === "drafts" ? "Keine Entwürfe" : "Noch keine Rechnungen"}</div>
            <p className="text-sm text-gray-500 mt-1">{mode === "drafts" ? "Neue Rechnungen werden zunächst als Entwurf angelegt und erscheinen hier bis zur Finalisierung." : "Erstellen Sie Ihre erste §25a-konforme Rechnung mit dem „Neue Rechnung“-Button."}</p>
        </div>
    );
    return (
        <div className="space-y-3">
            {/* Filter chips (nicht im Entwürfe-Tab) */}
            {mode !== "drafts" && (
            <div className="flex gap-1.5 flex-wrap" data-testid="invoice-filter-chips">
                {[
                    { k: "active", l: "Aktive", c: counts.active },
                    { k: "cancelled", l: "Storniert / Storno", c: counts.cancelled },
                    { k: "all", l: "Alle", c: counts.all },
                ].map((f) => (
                    <button
                        key={f.k}
                        type="button"
                        onClick={() => onFilterChange(f.k)}
                        className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors inline-flex items-center gap-1.5 ${filterStatus === f.k ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                        data-testid={`filter-${f.k}`}
                    >
                        {f.l}
                        <span className={`text-[10px] px-1.5 rounded-sm ${filterStatus === f.k ? "bg-white/20" : "bg-gray-100"}`}>{f.c}</span>
                    </button>
                ))}
            </div>
            )}

            {!visible.length ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-sm text-gray-500" data-testid="invoice-filter-empty">
                    Keine Rechnungen mit diesem Filter.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="invoice-list">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-600">
                            <tr>
                                <th className="text-left px-4 py-3 font-bold">Nummer</th>
                                <th className="text-left px-4 py-3 font-bold">Kunde</th>
                                {invoices.some((i) => i.site_label) && <th className="text-left px-4 py-3 font-bold">Standort</th>}
                                <th className="text-left px-4 py-3 font-bold">Datum</th>
                                <th className="text-right px-4 py-3 font-bold">Betrag</th>
                                <th className="text-center px-4 py-3 font-bold">Status</th>
                                <th className="text-right px-4 py-3 font-bold">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((inv) => {
                                const s = STATUS_LABEL[inv.status] || { label: inv.status, color: "bg-gray-100" };
                                const cust = custById[inv.customer_id] || {};
                                const custLabel = cust.type === "business" && cust.company_name
                                    ? cust.company_name
                                    : `${cust.first_name || ""} ${cust.last_name || ""}`.trim();
                                const isStorno = !!inv.is_storno;
                                const isCredit = !!inv.is_credit;
                                const isCancelled = inv.status === "cancelled";
                                const stornoRef = isCancelled && inv.cancelled_by_invoice_id ? inv.cancelled_by_invoice_number : null;
                                const originalRef = isStorno ? inv.storno_of_invoice_number : (isCredit ? inv.credit_note_of_invoice_number : null);
                                const canCancel = !isStorno && !isCredit && !isCancelled && ["final", "partial_paid", "paid", "reminded"].includes(inv.status);
                                // Jul-2026 (User-Wunsch): Rechnung mit vorhandener Gutschrift ist NICHT
                                // mehr stornierbar — entweder Voll-Storno ODER (Teil-)Gutschriften.
                                const hasCreditNote = (Array.isArray(inv.credit_notes) && inv.credit_notes.length > 0)
                                    || invoices.some((c) => c.is_credit && c.credit_note_of_invoice_id === inv.id);
                                const reminders = inv.reminders || [];
                                const maxRemLevel = reminders.reduce((m, r) => Math.max(m, Number(r.level) || 0), 0);
                                // Jul-2026 (User-Wunsch): Gutschriften und stornierte Rechnungen
                                // brauchen keine Zahlungserinnerung.
                                const canRemind = !isStorno && !isCancelled && !isCredit && ["final", "partial_paid", "reminded"].includes(inv.status) && maxRemLevel < 3;
                                // Jul-2026 (User-Wunsch) — Aktion bereits erfolgt → Button GRÜN
                                // gefüllt (keine grünen Umrandungen mehr als Standard).
                                const doneCls = "bg-green-600 border-green-600 text-white hover:bg-green-700 hover:text-white";
                                const aDone = (k) => !!(inv.action_log || {})[k];
                                return (
                                    <tr key={inv.id} className={`border-b border-gray-100 hover:bg-gray-50 ${isCancelled ? "bg-red-50/40" : ""} ${isStorno ? "bg-amber-50/40" : ""}`} data-testid={`inv-row-${inv.id}`}>
                                        <td className="px-4 py-3 font-mono-num font-bold">
                                            <div className="inline-flex items-center gap-1 group/nr">
                                                <button
                                                    type="button"
                                                    onClick={() => onDownload(inv.id, inv.invoice_number)}
                                                    title="Rechnungs-PDF öffnen"
                                                    className="text-left text-[#111827] hover:text-[#FFCC00] hover:underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFCC00] rounded-sm"
                                                    data-testid={`open-pdf-${inv.id}`}
                                                >
                                                    {inv.invoice_number || <span className="text-gray-600 underline decoration-dotted">ENTWURF</span>}
                                                </button>
                                                {inv.invoice_number && (
                                                    <CopyBtn value={inv.invoice_number} revealClass="group-hover/nr:opacity-100" testId={`copy-invnr-${inv.id}`} />
                                                )}
                                            </div>
                                            {isStorno && (
                                                <div className="mt-0.5">
                                                    <Badge className="bg-amber-200 text-amber-900 text-[9px] font-bold uppercase tracking-wider rounded-sm" data-testid={`storno-badge-${inv.id}`}>
                                                        STORNO
                                                    </Badge>
                                                </div>
                                            )}
                                            {isCredit && (
                                                <div className="mt-0.5">
                                                    <Badge className="bg-emerald-200 text-emerald-900 text-[9px] font-bold uppercase tracking-wider rounded-sm" data-testid={`credit-badge-${inv.id}`}>
                                                        GUTSCHRIFT
                                                    </Badge>
                                                </div>
                                            )}
                                            {originalRef && (
                                                <div className="text-[10px] text-gray-500 mt-0.5">→ zu {originalRef}</div>
                                            )}
                                            {stornoRef && (
                                                <div className="text-[10px] text-red-700 mt-0.5" data-testid={`cancelled-ref-${inv.id}`}>
                                                    storniert durch {stornoRef}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {/* Aug-2026 (User-Wunsch): Kundenname klickbar → Kundenakte */}
                                            {(inv.customer_name || custLabel) ? (
                                                <button type="button"
                                                    onClick={() => onOpenEntity && onOpenEntity("customers", inv.customer_name || custLabel)}
                                                    className="font-semibold text-[#111827] hover:text-[#1D4ED8] hover:underline underline-offset-2 text-left"
                                                    title="Kundenakte öffnen (alle Kundendaten)"
                                                    data-testid={`inv-customer-link-${inv.id}`}>
                                                    {inv.customer_name || custLabel}
                                                </button>
                                            ) : (
                                                <div className="font-semibold">—</div>
                                            )}
                                            {inv.status === "draft" && addressIssues(cust).length > 0 && (
                                                <div className="mt-1">
                                                    <button type="button"
                                                        onClick={() => onAddrFixToggle && onAddrFixToggle(inv.id)}
                                                        className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-900 bg-amber-100 border border-amber-300 rounded-sm px-1.5 py-0.5 hover:bg-amber-200"
                                                        title="Fehlende Adressfelder direkt ergänzen"
                                                        data-testid={`inv-addr-warning-${inv.id}`}>
                                                        <AlertTriangle className="w-3 h-3" /> Anschrift unvollständig: {addressIssues(cust).join(", ")}
                                                    </button>
                                                    {addrFixInvId === inv.id && (
                                                        <CustomerAddressQuickFix customer={cust}
                                                            onSaved={(u) => { onCustomerPatched && onCustomerPatched(u); onAddrFixToggle(null); }}
                                                            onClose={() => onAddrFixToggle(null)} />
                                                    )}
                                                </div>
                                            )}
                                            {inv.bearbeiter_name && (
                                                <div className="text-[10px] text-gray-500 mt-0.5" data-testid={`inv-bearbeiter-${inv.id}`}>
                                                    Bearbeiter: {inv.bearbeiter_name}{inv.bearbeiter_function ? ` · ${{ verkaeufer: "Verkäufer", kundendienst: "Kundendienst", monteur: "Monteur", mechaniker: "Monteur", filialleiter: "Filialleiter", verkaufsleiter: "Verkaufsleiter", buchhaltung: "Buchhaltung" }[inv.bearbeiter_function] || inv.bearbeiter_function}` : ""}
                                                </div>
                                            )}
                                            {(inv.customer_number || cust.customer_number) && (
                                                <div className="text-[10px] text-gray-500 font-mono-num" data-testid={`inv-custno-${inv.id}`}>
                                                    {inv.customer_number || cust.customer_number}
                                                </div>
                                            )}
                                            {inv.vehicle_info && (
                                                <div className="text-[10px] text-gray-600 font-mono-num mt-0.5 inline-flex items-center gap-1 group/vin flex-wrap" data-testid={`inv-vehicle-${inv.id}`}>
                                                    <span>{[inv.vehicle_info.brand, inv.vehicle_info.model].filter(Boolean).join(" ")}</span>
                                                    {/* Aug-2026 (User-Wunsch): FIN klickbar (so deutlich wie der Kundenname) → Fahrzeugakte */}
                                                    {inv.vehicle_info.vin && (
                                                        <button type="button"
                                                            onClick={() => onOpenEntity && onOpenEntity("vehicles", inv.vehicle_info.vin)}
                                                            className="font-semibold text-sm text-[#111827] hover:text-[#1D4ED8] hover:underline underline-offset-2 font-mono-num"
                                                            title="Fahrzeugakte öffnen (alle Fahrzeugdaten)"
                                                            data-testid={`inv-vin-link-${inv.id}`}>
                                                            FIN {inv.vehicle_info.vin}
                                                        </button>
                                                    )}
                                                    {inv.vehicle_info.vin && (
                                                        <CopyBtn value={inv.vehicle_info.vin} revealClass="group-hover/vin:opacity-100" testId={`copy-vin-${inv.id}`} />
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        {invoices.some((i) => i.site_label) && (
                                            <td className="px-4 py-3 text-gray-600" data-testid={`inv-site-${inv.id}`}>
                                                {inv.site_label ? (
                                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-700 bg-gray-100 border border-gray-200 rounded-sm px-1.5 py-0.5">
                                                        <MapPin className="w-3 h-3 text-gray-400" /> {inv.site_label}
                                                    </span>
                                                ) : "—"}
                                            </td>
                                        )}
                                        <td className="px-4 py-3 text-gray-600">{fmtDate(inv.invoice_date)}</td>
                                        <td className={`px-4 py-3 text-right font-mono-num font-bold ${isStorno ? "text-red-700" : ""}`}>
                                            {fmtEur(inv.totals?.total_gross)}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <Badge className={`${s.color} font-bold uppercase tracking-wider text-[10px] rounded-sm`} data-testid={`status-badge-${inv.id}`}>
                                                {s.label}
                                            </Badge>
                                            {maxRemLevel > 0 && (
                                                <div className="mt-1 flex justify-center" data-testid={`reminder-level-${inv.id}`}>
                                                    <Badge className="bg-orange-100 text-orange-800 border border-orange-300 font-bold text-[9px] rounded-sm uppercase tracking-wider">
                                                        <Bell className="w-2.5 h-2.5 mr-0.5 inline" />
                                                        {maxRemLevel === 1 ? "1. Erinnerung" : maxRemLevel === 2 ? "1. Mahnung" : "2. Mahnung"}
                                                    </Badge>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex gap-1 justify-end flex-wrap">
                                                <Button size="sm" variant="outline" onClick={() => onDownload(inv.id, inv.invoice_number)} className={`h-7 text-[11px] rounded-sm ${aDone("pdf") ? doneCls : ""}`} title={aDone("pdf") ? `PDF bereits geöffnet/heruntergeladen (${fmtDate(inv.action_log.pdf)})` : "Rechnungs-PDF öffnen"} data-testid={`download-${inv.id}`}>
                                                    <Download className="w-3 h-3 mr-1" /> PDF
                                                </Button>
                                                {inv.invoice_number && inv.status !== "draft" && (
                                                    <>
                                                        <Button size="sm" variant="outline" onClick={() => onDownloadXRechnung(inv.id, inv.invoice_number, false)} className={`h-7 text-[11px] rounded-sm ${aDone("xml") ? doneCls : ""}`} title="XRechnung 3.0 UBL (EN 16931, B2B/B2G)" data-testid={`xrechnung-xml-${inv.id}`}>
                                                            <Download className="w-3 h-3 mr-1" /> XML
                                                        </Button>
                                                        <Button size="sm" variant="outline" onClick={() => onDownloadXRechnung(inv.id, inv.invoice_number, true)} className={`h-7 text-[11px] rounded-sm ${aDone("zip") ? doneCls : ""}`} title="ZIP-Bundle: XRechnung-XML + PDF" data-testid={`xrechnung-zip-${inv.id}`}>
                                                            <Download className="w-3 h-3 mr-1" /> ZIP
                                                        </Button>
                                                    </>
                                                )}
                                                {inv.status === "draft" && (
                                                    <>
                                                    <Button size="sm" onClick={() => onFinalize(inv.id)} className={`h-7 text-[11px] rounded-sm text-white ${finalizeConfirmId === inv.id ? "bg-red-600 hover:bg-red-700" : "bg-[#111827] hover:bg-black"}`} data-testid={`finalize-${inv.id}`}>
                                                        <Lock className="w-3 h-3 mr-1" /> {finalizeConfirmId === inv.id ? "Bestätigen: Finalisieren" : "Finalisieren"}
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => onDeleteDraft(inv.id)}
                                                        className={`h-7 text-[11px] rounded-sm ${deleteConfirmId === inv.id ? "bg-red-600 border-red-600 text-white hover:bg-red-700 hover:text-white" : "border-red-300 text-red-600 hover:bg-red-50"}`}
                                                        title="Entwurf löschen (nur ohne Rechnungsnummer möglich, GoBD)"
                                                        data-testid={`delete-draft-${inv.id}`}>
                                                        <Trash2 className="w-3 h-3 mr-1" /> {deleteConfirmId === inv.id ? "Bestätigen: Löschen" : "Löschen"}
                                                    </Button>
                                                    </>
                                                )}
                                                {inv.b2b_deal_id && inv.status === "draft" && (
                                                    <Button size="sm" onClick={() => onFinalizeSendB2B(inv.id)}
                                                        className={`h-7 text-[11px] rounded-sm text-white ${b2bSendConfirmId === inv.id ? "bg-red-600 hover:bg-red-700" : "bg-green-700 hover:bg-green-800"}`}
                                                        title="Finalisiert die B2B-Rechnung und sendet sie automatisch als PDF an den Käufer-Händler"
                                                        data-testid={`finalize-send-b2b-${inv.id}`}>
                                                        <Receipt className="w-3 h-3 mr-1" /> {b2bSendConfirmId === inv.id ? "Bestätigen: Finalisieren & senden" : "Finalisieren & senden (B2B)"}
                                                    </Button>
                                                )}
                                                {!isStorno && !isCancelled && (inv.status === "final" || inv.status === "partial_paid" || inv.status === "reminded") && (
                                                    <>
                                                        <Button size="sm" variant="outline" onClick={() => onSendEmail(inv.id)} className={`h-7 text-[11px] rounded-sm ${inv.emailed_at ? doneCls : ""}`} title={inv.emailed_at ? `Bereits gesendet an ${inv.emailed_to || "Kunde"} (${fmtDate(inv.emailed_at)})` : "Rechnung per E-Mail senden (mit Anhängen möglich)"} data-testid={`email-${inv.id}`}>
                                                            <Receipt className="w-3 h-3 mr-1" /> E-Mail
                                                        </Button>
                                                        {waNumber(custById[inv.customer_id]?.phone) && (
                                                            <Button size="sm" variant="outline" onClick={() => waSendInvoice(inv)} className={`h-7 text-[11px] rounded-sm ${aDone("whatsapp") ? doneCls : ""}`} title="Rechnung per WhatsApp senden" data-testid={`whatsapp-${inv.id}`}>
                                                                <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                                                            </Button>
                                                        )}
                                                        {canMarkPaid ? (
                                                            <Button size="sm" variant="outline" onClick={() => onMarkPaid(inv.id, inv.totals?.total_gross)} className="h-7 text-[11px] rounded-sm" title="Zahlung manuell buchen (Buchhaltung)" data-testid={`mark-paid-${inv.id}`}>
                                                                <CheckCircle2 className="w-3 h-3 mr-1" /> Bezahlt
                                                            </Button>
                                                        ) : (
                                                            <Button size="sm" variant="outline" onClick={() => toast.info("Nur die Buchhaltung darf Rechnungen als bezahlt markieren.")} className="h-7 text-[11px] rounded-sm text-gray-400 cursor-not-allowed hover:bg-gray-50" title="Nur die Buchhaltung darf Zahlungen buchen (EC-Karte, Bankumsatz oder manuell)" data-testid={`mark-paid-${inv.id}`}>
                                                                <CheckCircle2 className="w-3 h-3 mr-1" /> Bezahlt
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                                {/* Aug-2026 (User-Wunsch) — Zahlart nachträglich setzen */}
                                                {!isStorno && !isCancelled && inv.status !== "draft" && (
                                                    <select value={payCodeOf(inv)}
                                                        onChange={(e) => onSetPayTerms && onSetPayTerms(inv.id, e.target.value)}
                                                        className="h-7 text-[11px] font-bold border border-gray-300 rounded-sm bg-white px-1 text-[#111827]"
                                                        title="Zahlart — erscheint auf der Rechnung als „Zahlung: …“"
                                                        data-testid={`pay-terms-${inv.id}`}>
                                                        <option value="">Zahlart…</option>
                                                        {PAY_TERM_OPTIONS.map(([k, label]) => (
                                                            <option key={k} value={k}>{label}</option>
                                                        ))}
                                                    </select>
                                                )}
                                                {/* Jul-2026 (User-Wunsch) — GRÜNER Bezahlt-Status sobald die
                                                    Zahlung gebucht wurde (EC, Bankumsatz, online oder manuell) */}
                                                {inv.status === "paid" && (
                                                    <Button size="sm" disabled className="h-7 text-[11px] rounded-sm bg-green-600 hover:bg-green-600 text-white opacity-100 cursor-default"
                                                        title={`Zahlung gebucht${inv.paid_at ? ` am ${fmtDate(inv.paid_at)}` : ""} · Quelle: ${({ ec: "EC-Karte", bank: "Bankumsatz", online: "Online-Zahlung", manuell: `manuell${inv.paid_by_name ? ` (${inv.paid_by_name})` : ""}` }[inv.paid_source]) || inv.payment_method || "gebucht"}`}
                                                        data-testid={`paid-badge-${inv.id}`}>
                                                        <CheckCircle2 className="w-3 h-3 mr-1" /> Bezahlt
                                                    </Button>
                                                )}
                                                {canRemind && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => onRemind(inv)}
                                                        className={`h-7 text-[11px] rounded-sm ${maxRemLevel > 0 ? doneCls : "border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800"}`}
                                                        title={maxRemLevel > 0 ? `Bereits gemahnt (Stufe ${maxRemLevel}) — nächste Stufe möglich` : "Zahlungserinnerung senden"}
                                                        data-testid={`remind-${inv.id}`}
                                                    >
                                                        <Bell className="w-3 h-3 mr-1" />
                                                        {maxRemLevel === 0 ? "Erinnern" : "Mahnen"}
                                                    </Button>
                                                )}
                                                {canCancel && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => onCreditNote(inv)}
                                                        className={`h-7 text-[11px] rounded-sm ${hasCreditNote ? doneCls : ""}`}
                                                        title={hasCreditNote ? "Gutschrift bereits erstellt — weitere Teil-Gutschrift möglich" : "Gutschrift erstellen"}
                                                        data-testid={`credit-note-${inv.id}`}
                                                    >
                                                        <Receipt className="w-3 h-3 mr-1" /> Gutschrift
                                                    </Button>
                                                )}
                                                {canCancel && !hasCreditNote && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => onCancel(inv)}
                                                        className="h-7 text-[11px] rounded-sm border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                                                        data-testid={`cancel-${inv.id}`}
                                                    >
                                                        <Ban className="w-3 h-3 mr-1" /> Stornieren
                                                    </Button>
                                                )}
                                                {canCancel && hasCreditNote && (
                                                    <span className="inline-flex items-center h-7 text-[10px] font-bold px-2 rounded-sm bg-gray-100 text-gray-500 border border-gray-200"
                                                        title="Zu dieser Rechnung existiert bereits eine Gutschrift — Storno gesperrt. Restbetrag ggf. per weiterer Gutschrift korrigieren."
                                                        data-testid={`cancel-locked-${inv.id}`}>
                                                        <Ban className="w-3 h-3 mr-1" /> Storno gesperrt (Gutschrift)
                                                    </span>
                                                )}
                                            </div>
                                            {reminders.length > 0 && (
                                                <div className="flex gap-1 justify-end mt-1.5 flex-wrap" data-testid={`reminder-history-${inv.id}`}>
                                                    {reminders.map((r) => {
                                                        const lvl = Number(r.level) || 1;
                                                        const lblShort = lvl === 1 ? "ZE" : lvl === 2 ? "M1" : "M2";
                                                        return (
                                                            <button
                                                                key={lvl}
                                                                type="button"
                                                                onClick={() => onDownloadReminder(inv.id, lvl, inv.invoice_number)}
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-orange-50 border border-orange-200 text-orange-800 hover:bg-orange-100"
                                                                title={`Mahnung Stufe ${lvl} · ${fmtDate(r.created_at)} · Gebühr ${fmtEur(r.fee)} · neue Fälligkeit ${fmtDate(r.new_due_date)}`}
                                                                data-testid={`reminder-pdf-${inv.id}-${lvl}`}
                                                            >
                                                                <Download className="w-2.5 h-2.5" /> {lblShort} PDF
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


// Jun-2026 (User-Wunsch) — Teil-/Wert-Gutschrift: Positionen der Originalrechnung
// auswählen/anpassen und mit eigener Nummer gutschreiben (Original bleibt gültig).
function CreditNoteModal({ invoice, customer, onClose, onCreated }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [rows, setRows] = useState(() => (invoice.items || []).map((it, i) => ({
        key: i,
        checked: false,
        title: it.description || it.title || `Position ${i + 1}`,
        quantity: Math.abs(Number(it.quantity) || 1),
        unit_price_net: Number(it.unit_price_net) || 0,
        vat_rate: Number(it.vat_rate ?? 19),
        is_differenzbesteuerung: !!it.is_differenzbesteuerung,
        purchase_price_gross: it.purchase_price_gross ?? null,
    })));
    const upd = (k, patch) => setRows((rs) => rs.map((r) => (r.key === k ? { ...r, ...patch } : r)));
    const selected = rows.filter((r) => r.checked && Number(r.quantity) > 0);
    const totalGross = selected.reduce((s, r) => {
        const net = Number(r.quantity) * Number(r.unit_price_net);
        return s + (r.is_differenzbesteuerung ? net : net * (1 + Number(r.vat_rate) / 100));
    }, 0);
    const submit = async () => {
        if (reason.trim().length < 3) { toast.error("Bitte einen Grund angeben (mind. 3 Zeichen)."); return; }
        if (!selected.length) { toast.error("Bitte mindestens eine Position auswählen."); return; }
        setBusy(true);
        try {
            const res = await api.post(`/invoices/${invoice.id}/credit-note`, {
                reason: reason.trim(),
                items: selected.map((r) => ({
                    description: r.title, quantity: Number(r.quantity), unit_price_net: Number(r.unit_price_net),
                    vat_rate: Number(r.vat_rate), is_differenzbesteuerung: r.is_differenzbesteuerung,
                    purchase_price_gross: r.purchase_price_gross,
                })),
            });
            toast.success(`Gutschrift ${res.data?.invoice_number || ""} erstellt`);
            try {
                const pdf = await api.get(`/invoices/${res.data.id}/pdf`, { responseType: "blob" });
                previewPdfResponse(pdf, `Gutschrift_${res.data?.invoice_number || ""}.pdf`, { recipient: customerRecipient(customer) });
            } catch { /* PDF optional */ }
            onCreated();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Gutschrift fehlgeschlagen");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="credit-note-modal">
            <div className="bg-white rounded-sm shadow-xl w-full max-w-2xl max-h-[88vh] overflow-y-auto p-5">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-black text-lg text-[#111827]">Gutschrift zu {invoice.invoice_number}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="credit-note-close"><X className="w-5 h-5" /></button>
                </div>
                <p className="text-xs text-gray-500 mb-3">Die Originalrechnung bleibt gültig — nur die ausgewählten Positionen/Beträge werden mit eigener Nummer gutgeschrieben. Menge und Preis sind anpassbar (z. B. Teil-Gutschrift).</p>
                <div className="space-y-2 mb-4">
                    {rows.map((r) => (
                        <div key={r.key} className={`flex items-center gap-2 border rounded-sm px-2 py-1.5 ${r.checked ? "border-emerald-400 bg-emerald-50/40" : "border-gray-200"}`} data-testid={`credit-item-${r.key}`}>
                            <input type="checkbox" checked={r.checked} onChange={(e) => upd(r.key, { checked: e.target.checked })} className="h-4 w-4 accent-emerald-600" data-testid={`credit-item-check-${r.key}`} />
                            <span className="flex-1 min-w-0 truncate text-sm font-bold text-[#111827]">{r.title}</span>
                            <Input type="number" min="0" step="0.5" value={r.quantity} onChange={(e) => upd(r.key, { quantity: e.target.value })} className="w-20 h-8 rounded-sm text-right" disabled={!r.checked} data-testid={`credit-item-qty-${r.key}`} />
                            <span className="text-[10px] text-gray-400">×</span>
                            <Input type="number" step="0.01" value={r.unit_price_net} onChange={(e) => upd(r.key, { unit_price_net: e.target.value })} className="w-28 h-8 rounded-sm text-right" disabled={!r.checked} data-testid={`credit-item-price-${r.key}`} />
                            <span className="text-[10px] text-gray-500 w-16">{r.is_differenzbesteuerung ? "§25a" : `netto, ${r.vat_rate}%`}</span>
                        </div>
                    ))}
                </div>
                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Grund der Gutschrift *</label>
                    <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z.B. Kulanz, Preisnachlass, mangelhafte Leistung" maxLength={500} className="rounded-sm mt-1" data-testid="credit-note-reason" />
                </div>
                <div className="flex items-center justify-between mt-4">
                    <div className="text-sm font-black text-emerald-700" data-testid="credit-note-total">
                        Gutschrift: {totalGross.toLocaleString("de-DE", { minimumFractionDigits: 2 })} € brutto
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="credit-note-cancel">Abbrechen</Button>
                        <Button onClick={submit} disabled={busy || !selected.length} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="credit-note-submit">
                            {busy ? "Erstelle …" : "Gutschrift erstellen"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}


function StornoModal({ target, onClose, onConfirm }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (reason.trim().length < 3) {
            toast.error("Bitte einen Storno-Grund (mind. 3 Zeichen) angeben");
            return;
        }
        setBusy(true);
        await onConfirm(reason.trim());
        setBusy(false);
    };
    return (
        <ModalShell onClose={onClose} title={`Rechnung ${target.invoice_number || ""} stornieren`} testid="storno-modal">
            <form onSubmit={submit} className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-sm">
                    <div className="flex gap-2 items-start text-xs text-red-900">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                            <strong>GoBD-konformer Storno:</strong> Es wird eine separat nummerierte
                            <em> Storno-Rechnung</em> mit negativen Beträgen erstellt. Die ursprüngliche
                            Rechnung bleibt im System (Append-Only) und wird als „Storniert" markiert.
                            Bereits gezahlte Beträge müssen separat zurückerstattet werden.
                        </div>
                    </div>
                </div>
                <div className="text-sm text-gray-700">
                    Ursprünglicher Betrag: <strong className="font-mono-num">{fmtEur(target.total)}</strong>
                </div>
                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Storno-Grund *</label>
                    <Textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="rounded-sm mt-1 min-h-[80px]"
                        placeholder="z. B. Käuferrücktritt nach §437 BGB · Falsche Bankverbindung · Fehlerhafte Position …"
                        data-testid="storno-reason"
                        autoFocus
                    />
                    <div className="text-[10px] text-gray-500 mt-1">Pflichtfeld — wird im Audit-Log und auf der Storno-PDF gespeichert.</div>
                </div>
                <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-sm" data-testid="storno-cancel">
                        Abbrechen
                    </Button>
                    <Button
                        type="submit"
                        disabled={busy || reason.trim().length < 3}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-black rounded-sm uppercase tracking-wider disabled:opacity-50"
                        data-testid="storno-confirm"
                    >
                        <Ban className="w-4 h-4 mr-1.5" />
                        {busy ? "Storniere…" : "Storno bestätigen"}
                    </Button>
                </div>
            </form>
        </ModalShell>
    );
}

function ReminderModal({ target, onClose, onConfirm }) {
    const existingLevels = (target.reminders || []).map((r) => Number(r.level));
    const maxLevel = existingLevels.reduce((m, l) => Math.max(m, l || 0), 0);
    const nextLevel = Math.min(3, maxLevel + 1);
    const DEFAULT_FEES = { 1: 0, 2: 5, 3: 10 };
    const LEVEL_LABELS = {
        1: "Zahlungserinnerung (freundlich)",
        2: "1. Mahnung (formell + Gebühr)",
        3: "2. Mahnung — Letzte Aufforderung (Inkasso-Hinweis)",
    };
    const [level, setLevel] = useState(nextLevel);
    const [fee, setFee] = useState(DEFAULT_FEES[nextLevel]);
    const [note, setNote] = useState("");
    const [dueDays, setDueDays] = useState(7);
    const [sendEmail, setSendEmail] = useState(true);
    const [busy, setBusy] = useState(false);

    const handleLevelChange = (lvl) => {
        const n = Number(lvl);
        setLevel(n);
        setFee(DEFAULT_FEES[n] ?? 0);
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        await onConfirm({
            level: Number(level),
            fee: Number(fee),
            note: note.trim() || null,
            new_due_days: Number(dueDays) || 7,
            send_email: sendEmail,
        });
        setBusy(false);
    };

    const totalGross = Number(target.total || 0);
    const paid = Number(target.paid_amount || 0);
    const openAmount = Math.max(0, totalGross - paid);
    const totalDue = (openAmount + Number(fee || 0)).toFixed(2);

    return (
        <ModalShell onClose={onClose} title={`Mahnung zu Rechnung ${target.invoice_number || ""}`} testid="reminder-modal">
            <form onSubmit={submit} className="space-y-4">
                <div className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded-sm">
                    <div className="flex gap-2 items-start text-xs text-orange-900">
                        <Bell className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                            <strong>Eskalations-Stufen:</strong> 1 = freundliche Zahlungserinnerung (ohne Gebühr) ·
                            2 = formelle 1. Mahnung (Standard 5 €) · 3 = letzte Mahnung mit Inkasso-Hinweis (Standard 10 €).
                            Pro Stufe nur einmal möglich. Status der Rechnung wird auf „Gemahnt" gesetzt.
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-2.5">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Rechnungsbetrag</div>
                        <div className="font-mono-num font-bold">{fmtEur(totalGross)}</div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-2.5">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Offen</div>
                        <div className="font-mono-num font-bold">{fmtEur(openAmount)}</div>
                    </div>
                </div>

                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Mahnstufe *</label>
                    <Select value={String(level)} onValueChange={handleLevelChange}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="rem-level"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {[1, 2, 3].filter((l) => !existingLevels.includes(l)).map((l) => (
                                <SelectItem key={l} value={String(l)}>Stufe {l} — {LEVEL_LABELS[l]}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {maxLevel > 0 && (
                        <div className="text-[10px] text-gray-500 mt-1">Bereits versendet: Stufe(n) {existingLevels.join(", ")}</div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Mahngebühr (€)</label>
                        <Input
                            type="number"
                            min="0"
                            max="50"
                            step="0.5"
                            value={fee}
                            onChange={(e) => setFee(e.target.value)}
                            className="rounded-sm mt-1"
                            data-testid="rem-fee"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Neue Fälligkeit (Tage)</label>
                        <Input
                            type="number"
                            min="1"
                            max="60"
                            step="1"
                            value={dueDays}
                            onChange={(e) => setDueDays(e.target.value)}
                            className="rounded-sm mt-1"
                            data-testid="rem-due-days"
                        />
                    </div>
                </div>

                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Interne Notiz (optional)</label>
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        className="rounded-sm mt-1 min-h-[60px]"
                        placeholder="z. B. Telefonat geführt am … · Erreichbar zwischen 9–12 Uhr · …"
                        data-testid="rem-note"
                    />
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        data-testid="rem-send-email"
                    />
                    <Mail className="w-4 h-4 text-gray-600" />
                    <span>Mahnung direkt per E-Mail an Kunden versenden (PDF-Anhang)</span>
                </label>

                <div className="bg-[#111827] text-white rounded-sm p-3 flex justify-between items-center">
                    <div className="text-xs uppercase tracking-wider font-bold">Neuer Gesamtbetrag</div>
                    <div className="font-mono-num font-black text-lg">{fmtEur(totalDue)}</div>
                </div>

                <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-sm" data-testid="rem-cancel">
                        Abbrechen
                    </Button>
                    <Button
                        type="submit"
                        disabled={busy}
                        className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-black rounded-sm uppercase tracking-wider disabled:opacity-50"
                        data-testid="rem-confirm"
                    >
                        <Bell className="w-4 h-4 mr-1.5" />
                        {busy ? "Sende…" : "Mahnung erstellen"}
                    </Button>
                </div>
            </form>
        </ModalShell>
    );
}

export { CreateInvoiceModal };
export { VehiclesTab, BranchBreakdown, CustomerSegmentBar, CustomerList, CreateCustomerModal };
